import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * ClubCard - Tarjeta de Club de Pádel
 */
const ClubCard = ({ club, onClick, className = '' }) => {
    const navigate = useNavigate();

    // Normalizar datos (soportar formatos n/d/t/p del JSON local y campos completos de Firestore)
    // Buscamos en el objeto y en un posible sub-objeto data
    const d = club.data || club;
    
    // El JSON usa 'n' para nombre, 'd' para dirección, 't' para teléfono, 'p' para foto.
    // Firestore usa 'name', 'address', 'phone', 'photoUrl'.
    const name = d.n || d.name || d.displayName || 'Club sin nombre';
    const address = d.d || d.address || d.location || 'Dirección no disponible';
    const phone = d.t || d.phone || d.telephone || '';
    const photoUrlAlt = d.p || d.photoUrl || d.photo || '';
    const displayImage = photoUrlAlt || '/padel_lines.png';
    const id = club.id || d.id;
    const distance = club.distance !== undefined ? club.distance : d.distance;

    // Log estructurado para depuración
    console.group(`ClubCard: ${name}`);
    console.log('Original Item:', club);
    console.log('Normalized Data:', { name, address, phone, id, distance });
    console.groupEnd();

    const handleClick = () => {
        if (onClick) {
            onClick(club);
        } else {
            // Navegamos a la ficha del club usando el ID o un slug generado
            const clubId = id || `local-${encodeURIComponent(name)}`;
            navigate(`/clubs/${clubId}`);
        }
    };

    return (
        <div className={`bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden relative group ${className}`}>
            
            {/* Header con imagen */}
            <div 
                className="relative h-44 bg-padel-branded overflow-hidden cursor-pointer border-b border-gray-100"
                onClick={handleClick}
            >
                <img 
                    src={displayImage} 
                    alt={name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out opacity-90"
                    loading="lazy"
                />
                <div className="absolute top-3 right-3 flex gap-2 z-10 pointer-events-none">
                    <span className="bg-primary-600 text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-lg uppercase tracking-widest flex items-center gap-1.5">
                        <span>🏟️</span>
                        <span>Club de Pádel</span>
                    </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
                <div onClick={handleClick} className="cursor-pointer group-hover:text-primary-600 transition-colors">
                    <h3 className="text-xl font-bold text-gray-900 line-clamp-1 mb-1" title={name}>
                        {name}
                        {/* Debug only if name is missing */}
                        {!d.n && !d.name && <span className="text-[8px] text-red-500 ml-2">({Object.keys(d).join(',')})</span>}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="text-base flex-shrink-0">📍</span>
                        <span className="line-clamp-2">{address}</span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center justify-between pt-2">
                    {phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="text-base flex-shrink-0">📞</span>
                            <span className="font-medium">{phone}</span>
                        </div>
                    )}
                    
                    {distance !== undefined && distance !== null && (
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary-600 bg-primary-50 px-2 py-1 rounded-lg">
                            <span>🚗 A {distance.toFixed(1)} km</span>
                        </div>
                    )}
                </div>

                <button 
                    className="w-full bg-[#1e293b] text-white font-black py-4 px-4 rounded-xl shadow-xl hover:bg-[#0f172a] transition-all transform active:scale-95 flex items-center justify-center gap-3 mt-4 border-2 border-white/10 uppercase tracking-widest text-xs"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                    }}
                >
                    <span>Mas información</span>
                    <span className="text-xl group-hover:translate-x-1 transition-transform">➡️</span>
                </button>
            </div>
        </div>
    );
};

ClubCard.Skeleton = () => (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden animate-pulse">
        <div className="h-44 bg-gray-200" />
        <div className="p-5 space-y-4">
            <div className="h-6 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-10 bg-gray-200 rounded mt-4" />
        </div>
    </div>
);

export default ClubCard;
